import {
  transformFiltersToPreferences,
  transformFiltersToUnitsSearchParams,
  type Filters,
  type TrackingEventWithUnits,
} from "@superbright/indexeddb-orm";
import { type UseQueryOptions } from "@tanstack/react-query";

import { apiPaths, queryKeys } from "api/config";
import { transformListingsApiResponse } from "api/listings/transformListingsResponse";
import { useSaveFilters } from "api/save_filters/useSaveFilters";
import { type FiltersResponse, FiltersResponseSchema } from "api/schemas";
import type { FetchFiltersRequest } from "features/filters/types/filters.types";
import type { ListingsClientResponse } from "features/results/types/results.types";
import { type IQueryParams } from "types";
import { buildUrl, queryClient, useQuery } from "utils";
import { inresiHttpService } from "utils/http";

// ── FILTERS ──────────────────────────────────────────────────────────────────
export const getFiltersQueryKey = (apiParams: FetchFiltersRequest) =>
  queryKeys.filters(apiParams);

export const getFiltersQuery = (
  propertySlug: string,
  apiParams: FetchFiltersRequest
) => ({
  queryKey: [...getFiltersQueryKey(apiParams), propertySlug],
  queryFn: async (): Promise<FiltersResponse> => {
    const response = await inresiHttpService.post<FiltersResponse>(
      buildUrl(apiPaths.filters(propertySlug)),
      apiParams,
      {
        headers: { "Content-Type": "application/json" },
        schema: FiltersResponseSchema,
      }
    );
    return { data: response.data };
  },
  keepPreviousData: true,
  refetchOnWindowFocus: false,
});

export const useFiltersQuery = (
  propertySlug: string,
  rawFilters: Filters,
  options?: UseQueryOptions<FiltersResponse>
) => {
  const apiParams = transformFiltersToPreferences(rawFilters);
  return useQuery({ ...getFiltersQuery(propertySlug, apiParams), ...options });
};

// ── UNITS ────────────────────────────────────────────────────────────────────
export const getUnitsQueryKey = (apiParams: IQueryParams) =>
  queryKeys.listings(apiParams);

export const getUnitsQuery = (
  propertySlug: string,
  apiParams: IQueryParams
) => ({
  queryKey: [...getUnitsQueryKey(apiParams), propertySlug],
  queryFn: async (): Promise<ListingsClientResponse> => {
    const visitor_uuid = queryClient.getQueryData<string>(["visitorUUID"]);
    const response = await inresiHttpService.post<{
      data: Record<string, unknown>;
    }>(
      buildUrl(apiPaths.listings(propertySlug)),
      { ...apiParams, ...(visitor_uuid && { visitor_uuid }) },
      { headers: { "Content-Type": "application/json" } }
    );
    return transformListingsApiResponse(
      response.data as Parameters<typeof transformListingsApiResponse>[0],
      propertySlug
    );
  },
  keepPreviousData: true,
  refetchOnWindowFocus: false,
});

export const useUnitsQuery = (
  propertySlug: string,
  rawFilters: Filters,
  options?: UseQueryOptions<ListingsClientResponse>
) => {
  const unitParams = transformFiltersToUnitsSearchParams(rawFilters);

  return useQuery({
    ...getUnitsQuery(propertySlug, unitParams),
    ...options,
  });
};

// ── COMBINED: optional chain units after filters ─────────────────────────────
type CombinedOptions = {
  /** If true, after filters succeed, immediately fetch units with unitsRawFilters */
  triggerUnitsOnFiltersSuccess?: boolean;
  /** If true, after filters succeed, POST the filter preferences to save_filters */
  saveFiltersOnSuccess?: boolean;
  /** Override which filters power the units call; defaults to same raw filters */
  unitsRawFilters?: Filters;
  /** Pass-through options */
  filtersOptions?: UseQueryOptions<FiltersResponse>;
  unitsOptions?: UseQueryOptions<ListingsClientResponse>;

  setTrackingEventWithUnits?: (event: TrackingEventWithUnits | null) => void;
  trackingEventWithUnits?: TrackingEventWithUnits | null;
  /** Called when a filter change comes back with zero units */
  onZeroResultsAfterFilter?: (
    event: Omit<TrackingEventWithUnits, "trackingEvent">
  ) => void;
};

export function useFiltersAndUnits(
  propertySlug: string,
  filtersRaw: Record<string, unknown>,
  {
    triggerUnitsOnFiltersSuccess = false,
    saveFiltersOnSuccess = false,
    unitsRawFilters: _unitsRawFilters,
    filtersOptions,
    unitsOptions,
    trackingEventWithUnits,
    onZeroResultsAfterFilter,
    setTrackingEventWithUnits,
  }: CombinedOptions = {}
) {
  const filtersApiParams = transformFiltersToPreferences(filtersRaw);
  const unitsApiParams = transformFiltersToUnitsSearchParams(filtersRaw);
  const saveFiltersMutation = useSaveFilters(
    saveFiltersOnSuccess ? propertySlug : undefined
  );

  const filtersQuery = useQuery({
    ...getFiltersQuery(propertySlug, filtersApiParams),
    ...filtersOptions,
    onSuccess: (data) => {
      filtersOptions?.onSuccess?.(data);
      if (saveFiltersOnSuccess) {
        // Persist the full store shape (per-group keys) so bootstrap can restore
        // Sunny/Highlights/etc. independently — not just the flattened preferences.
        saveFiltersMutation.mutate(filtersRaw, {
          onSuccess: () => {
            if (triggerUnitsOnFiltersSuccess) {
              void queryClient.fetchQuery(
                getUnitsQuery(propertySlug, unitsApiParams)
              );
            }
          },
        });
      } else if (triggerUnitsOnFiltersSuccess) {
        void queryClient.fetchQuery(
          getUnitsQuery(propertySlug, unitsApiParams)
        );
      }
    },
  });

  const unitsQuery = useQuery({
    ...getUnitsQuery(propertySlug, unitsApiParams),
    ...unitsOptions,
    enabled:
      unitsOptions?.enabled !== undefined
        ? unitsOptions.enabled
        : !triggerUnitsOnFiltersSuccess,
    onSuccess: (data) => {
      unitsOptions?.onSuccess?.(data);
      if (trackingEventWithUnits && setTrackingEventWithUnits) {
        const { trackingEvent, ...rest } = trackingEventWithUnits;
        trackingEvent({ ...rest });
        const total = data.all.total + data.bestFits.total + data.closest.total;
        if (total === 0) onZeroResultsAfterFilter?.(rest);
        setTrackingEventWithUnits(null);
      }
    },
    onError: (error) => {
      unitsOptions?.onError?.(error);
    },
  });

  const refetchUnitsNow = () =>
    queryClient.fetchQuery(getUnitsQuery(propertySlug, unitsApiParams));

  return { filtersQuery, unitsQuery, refetchUnitsNow, saveFiltersMutation };
}

// ── Helpers ──────────────────────────────────────────────────────────────────
export const invalidateFilters = async (raw: Record<string, unknown>) => {
  const apiParams = transformFiltersToPreferences(raw);
  await queryClient.invalidateQueries({
    queryKey: getFiltersQueryKey(apiParams),
  });
};

export const invalidateUnits = async (raw: Record<string, unknown>) => {
  const apiParams = transformFiltersToUnitsSearchParams(raw);
  await queryClient.invalidateQueries({
    queryKey: getUnitsQueryKey(apiParams),
  });
};

export const updateUnit = async (unit: {
  id: string | number;
}): Promise<void> => {
  await inresiHttpService.put(buildUrl(`units/${unit.id}`), unit);
  await queryClient.invalidateQueries({ queryKey: ["units"] });
};
